import { useState } from 'react';
import { Button, Field, Input, Select } from '@/ui/primitives';
import { useQualificar, useUsers } from '../../api';
import { TEMP_CHIP } from '../../temperatura';
import {
  PERFIL_OPCOES,
  type Detail,
  type Temperatura,
} from '../../schemas';
import { usePerfilForm } from '../../hooks/usePerfilForm';

interface Props {
  id: string;
  cardId: string;
  detail: Detail | undefined;
  clienteCpf: string | null | undefined;
}

function formatDate(s: string | null | undefined) {
  if (!s) return null;
  try { return new Date(s).toLocaleString('pt-BR'); } catch { return s; }
}

type PerfilOpcao = { v: string; label: string };

function PerfilSelect({
  id, value, onChange, options,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
  options: ReadonlyArray<PerfilOpcao>;
}) {
  const matches = options.some((o) => o.label === value);
  return (
    <Select id={id} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">Selecione…</option>
      {options.map((o) => (
        <option key={o.v} value={o.label}>{o.label}</option>
      ))}
      {value && !matches && <option value={value}>{value}</option>}
    </Select>
  );
}

// Fase Qualificação — temperatura comercial, motivo, comercial responsável,
// potencial, prazo, observação + perfil comportamental do cliente (autosave).
// Avança pra Dossiê ao confirmar.
export function FaseQualificacao({ id, cardId, detail, clienteCpf }: Props) {
  const qualificar = useQualificar(id);
  const perfilSalvo = detail?.perfil ?? null;
  const perfil = usePerfilForm(detail, clienteCpf, cardId);
  const usersQ = useUsers();
  const comerciais = (usersQ.data ?? []).filter((u) =>
    u.role && ['admin', 'gestor', 'colaborador'].includes(u.role),
  );

  const [qTemp, setQTemp] = useState<Temperatura | ''>('');
  const [qComercial, setQComercial] = useState<string>('');
  const [qPotencialMin, setQPotencialMin] = useState('');
  const [qPotencialMax, setQPotencialMax] = useState('');
  const [qPrazo, setQPrazo] = useState('');
  const [qMotivo, setQMotivo] = useState('');
  const [qObs, setQObs] = useState('');

  function onSubmit() {
    if (!qTemp) return;
    const temPerfil = Object.keys(perfil.payload).length > 1 || perfil.fields.jaRecusou;
    qualificar.mutate({
      temperatura: qTemp,
      ...(qComercial && { comercial_id: Number(qComercial) }),
      ...(qPotencialMin && { potencial_min: Number(qPotencialMin) }),
      ...(qPotencialMax && { potencial_max: Number(qPotencialMax) }),
      ...(qPrazo && { prazo_abordagem: qPrazo }),
      ...(qMotivo && { motivo_qualificacao: qMotivo }),
      ...(qObs.trim() && { observacao: qObs.trim() }),
      ...(temPerfil && { perfil: perfil.payload }),
    }, {
      onSuccess: () => {
        setQTemp('');
        setQComercial('');
        setQPotencialMin('');
        setQPotencialMax('');
        setQPrazo('');
        setQMotivo('');
        setQObs('');
      },
    });
  }

  const { fields, setters, status } = perfil;

  return (
    <section className="border border-border rounded-lg p-4 bg-elevated flex flex-col gap-4">
      <div>
        <h3 className="text-[0.78rem] font-semibold uppercase tracking-wide text-text-muted">
          Qualificar oportunidade
        </h3>
        <p className="text-[0.78rem] text-text-2 mt-1">
          Registre o que justifica a qualificação. Esses campos viram histórico e contexto pro dossiê.
        </p>
      </div>

      <div className="flex flex-col gap-1.5">
        <span className="text-[0.72rem] font-semibold uppercase tracking-wider text-text-2">
          Temperatura comercial *
        </span>
        <div className="flex flex-wrap gap-2">
          {(['quente', 'morna', 'fria'] as const).map((t) => {
            const active = qTemp === t;
            const cls = active ? TEMP_CHIP[t] : TEMP_CHIP[t] + ' opacity-45 hover:opacity-75';
            return (
              <button key={t} type="button" onClick={() => setQTemp(t)}
                className={'px-3 py-1 rounded-md text-[0.82rem] font-semibold border-0 capitalize transition-colors ' + cls}>
                {t}
              </button>
            );
          })}
        </div>
      </div>

      <Field label="Motivo da qualificação" htmlFor="crm-q-motivo">
        <Select id="crm-q-motivo" value={qMotivo} onChange={(e) => setQMotivo(e.target.value)}>
          <option value="">Selecione um critério</option>
          <option value="urgencia_regulatoria">Urgência regulatória (prazo legal / risco de autuação)</option>
          <option value="risco_credito">Bloqueio de acesso a crédito rural</option>
          <option value="ticket_alto">Ticket alto / portfolio relevante do cliente</option>
          <option value="cliente_engajado">Cliente já demonstrou interesse / canal aberto</option>
          <option value="janela_safra">Janela de safra / sazonalidade</option>
          <option value="dependencia_outro_servico">Depende de outro serviço já contratado</option>
          <option value="baixa_prioridade">Baixa prioridade / sem urgência identificada</option>
          <option value="outro">Outro (descrever na observação)</option>
        </Select>
      </Field>

      <Field label="Responsável comercial" htmlFor="crm-q-comercial">
        <Select id="crm-q-comercial" value={qComercial} onChange={(e) => setQComercial(e.target.value)}>
          <option value="">Sem responsável definido</option>
          {comerciais.map((u) => (
            <option key={u.id} value={String(u.id)}>
              {u.name ?? u.email ?? `usuário ${u.id}`}
              {u.cargo ? ` · ${u.cargo}` : ''}
            </option>
          ))}
        </Select>
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Potencial mín. (R$)" htmlFor="crm-q-pmin">
          <Input id="crm-q-pmin" type="number" min="0" step="100" value={qPotencialMin} onChange={(e) => setQPotencialMin(e.target.value)} placeholder="ex: 5000" />
        </Field>
        <Field label="Potencial máx. (R$)" htmlFor="crm-q-pmax">
          <Input id="crm-q-pmax" type="number" min="0" step="100" value={qPotencialMax} onChange={(e) => setQPotencialMax(e.target.value)} placeholder="ex: 15000" />
        </Field>
      </div>

      <Field label="Prazo sugerido para abordagem" htmlFor="crm-q-prazo">
        <Input id="crm-q-prazo" type="date" value={qPrazo} onChange={(e) => setQPrazo(e.target.value)} />
      </Field>

      <Field label="Observação / contexto" htmlFor="crm-q-obs">
        <textarea
          id="crm-q-obs" value={qObs} onChange={(e) => setQObs(e.target.value)} rows={3}
          placeholder="Resumo do que conversou com o cliente, sinais de interesse, restrições, etc."
          className="w-full rounded-md bg-elevated border-[1.5px] border-border2 px-3 py-2 text-sm text-text placeholder:text-text-muted hover:border-[var(--text-muted)] focus:border-accent focus:outline-none focus:shadow-[0_0_0_3px_rgba(233,106,69,0.15)] resize-none"
        />
      </Field>

      <div className="border-t border-border pt-4 mt-2 flex flex-col gap-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h4 className="text-[0.78rem] font-semibold uppercase tracking-wide text-text">
              Perfil comportamental do cliente
            </h4>
            <p className="text-[0.78rem] text-text-2 mt-1">
              Escolha uma opção em cada campo. Salva automaticamente.
            </p>
          </div>
          <span
            className={
              'text-[0.72rem] font-medium ' +
              (status === 'salvando'
                ? 'text-text-muted'
                : status === 'salvo'
                ? 'text-[var(--ok)]'
                : status === 'erro'
                ? 'text-danger'
                : 'text-text-muted')
            }
          >
            {status === 'salvando' && 'salvando…'}
            {status === 'salvo' && '✓ salvo'}
            {status === 'erro' && 'erro ao salvar'}
            {status === 'idle' && perfilSalvo?.atualizado_em && (`salvo em ${formatDate(perfilSalvo.atualizado_em)}`)}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Decisor principal" htmlFor="crm-p-decisor">
            <PerfilSelect id="crm-p-decisor" value={fields.decisor} onChange={setters.setDecisor} options={PERFIL_OPCOES.decisor} />
          </Field>
          <Field label="Velocidade de decisão" htmlFor="crm-p-veloc">
            <PerfilSelect id="crm-p-veloc" value={fields.velocidade} onChange={setters.setVelocidade} options={PERFIL_OPCOES.velocidade_decisao} />
          </Field>
          <Field label="Sensibilidade a preço" htmlFor="crm-p-preco">
            <PerfilSelect id="crm-p-preco" value={fields.sensibilidade} onChange={setters.setSensibilidade} options={PERFIL_OPCOES.sensibilidade_preco} />
          </Field>
          <Field label="Reação a problema regulatório" htmlFor="crm-p-reacao">
            <PerfilSelect id="crm-p-reacao" value={fields.reacao} onChange={setters.setReacao} options={PERFIL_OPCOES.reacao_regulatorio} />
          </Field>
          <Field label="Status do relacionamento" htmlFor="crm-p-rel">
            <PerfilSelect id="crm-p-rel" value={fields.statusRel} onChange={setters.setStatusRel} options={PERFIL_OPCOES.status_relacionamento} />
          </Field>
          <Field label="Conhecimento técnico" htmlFor="crm-p-conh">
            <PerfilSelect id="crm-p-conh" value={fields.conhecimento} onChange={setters.setConhecimento} options={PERFIL_OPCOES.conhecimento_tema} />
          </Field>
          <Field label="Canal preferido" htmlFor="crm-p-canal">
            <PerfilSelect id="crm-p-canal" value={fields.canal} onChange={setters.setCanal} options={PERFIL_OPCOES.canal_preferido} />
          </Field>
          <Field label="Maturidade da operação" htmlFor="crm-p-mat">
            <PerfilSelect id="crm-p-mat" value={fields.maturidade} onChange={setters.setMaturidade} options={PERFIL_OPCOES.maturidade_operacao} />
          </Field>
        </div>

        <div className="flex flex-col gap-2">
          <label className="flex items-center gap-2 text-[0.82rem] text-text-2 cursor-pointer select-none">
            <input
              type="checkbox" checked={fields.jaRecusou}
              onChange={(e) => setters.setJaRecusou(e.target.checked)}
              className="rounded border-border2"
            />
            Já recusou serviços antes
          </label>
          {fields.jaRecusou && (
            <Field label="Qual recusa e por quê" htmlFor="crm-p-detrec">
              <Input
                id="crm-p-detrec" value={fields.detalheRecusas}
                onChange={(e) => setters.setDetalheRecusas(e.target.value)}
                placeholder="ex: laudo PRODES — achou caro"
              />
            </Field>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 border-t border-border pt-4 mt-2">
        <Button variant="primary" size="sm" onClick={onSubmit} disabled={qualificar.isPending || !qTemp}>
          {qualificar.isPending ? 'Qualificando…' : 'Qualificar e avançar'}
        </Button>
        <span className="text-[0.78rem] text-text-muted">
          {qTemp ? 'Avança para Dossiê e atualiza o perfil do cliente.' : '* temperatura é obrigatória'}
        </span>
      </div>
    </section>
  );
}
